import {
  setAssessment,
  setAssessmentReports,
  setAssessors,
  setAssessees,
  setAssesseeReports,
  setAssessments,
} from "reduxStore/state";

const BASE_URL = process.env.REACT_APP_BASE_URL;

/**
 * Fetch assessment reports with optional filters
 */
export const fetchAssessmentReports =
  ({
    organisation,
    location,
    division,
    level,
    assessor,
    assessee,
    startDate,
    endDate,
    selectedPrinciple,
  } = {}) =>
  async (dispatch, getState) => {
    try {
      const token = getState().token;

      const queryParams = new URLSearchParams({
        ...(organisation && { organisation }),
        ...(location && { location }),
        ...(division && { division }),
        ...(level && { level }),
        ...(assessor && { assessor }),
        ...(assessee && { assessee }),
        ...(startDate && { startDate }),
        ...(endDate && { endDate }),
        ...(selectedPrinciple && { selectedPrinciple }),
      }).toString();

      const response = await fetch(
        `${BASE_URL}/assessments/reports?${queryParams}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) throw new Error("Failed to fetch assessment reports");

      const reports = await response.json();
      dispatch(setAssessmentReports(reports));

      return reports;
    } catch (error) {
      console.error("Error while fetching assessment reports:", error);
      return { error: error.message };
    }
  };

export const fetchAssessmentDrilldown =
  ({
    organisation,
    location,
    division,
    level,
    assessor,
    assessee,
    startDate,
    endDate,
    questionId,
    principleId,
    rating,
    questionText,
    principleNumber,
  } = {}) =>
  async (dispatch, getState) => {
    try {
      const token = getState().token;

      const queryParams = new URLSearchParams({
        ...(organisation && { organisation }),
        ...(location && { location }),
        ...(division && { division }),
        ...(level && { level }),
        ...(assessor && { assessor }),
        ...(assessee && { assessee }),
        ...(startDate && { startDate }),
        ...(endDate && { endDate }),
        ...(questionId && { questionId }),
        ...(principleId && { principleId }),
        ...(rating && { rating }),
        ...(questionText && { questionText }),
        ...(principleNumber && { principleNumber }),
      }).toString();

      const response = await fetch(
        `${BASE_URL}/assessments/reports/drilldown?${queryParams}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (!response.ok) throw new Error("Failed to fetch drilldown data");

      return await response.json();
    } catch (error) {
      console.error("Error while fetching drilldown data:", error);
      return { error: error.message, entries: [] };
    }
  };

export const fetchAssesseeQuestionReport =
  ({ assessee, startDate, endDate, selectedPrinciple }) =>
  async (dispatch, getState) => {
    try {
      const token = getState().token;

      if (!assessee) {
        throw new Error("Assessee ID is required to fetch the report.");
      }

      const queryParams = new URLSearchParams({
        assessee,
        ...(startDate && { startDate }),
        ...(endDate && { endDate }),
        ...(selectedPrinciple && { selectedPrinciple }),
      }).toString();

      const response = await fetch(
        `${BASE_URL}/assessments/reports/assessee?${queryParams}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok)
        throw new Error("Failed to fetch assessee question report");

      const report = await response.json();

      dispatch(setAssessmentReports(report));

      return report;
    } catch (error) {
      console.error("Error while fetching assessee question report:", error);
      return { error: error.message };
    }
  };

/**
 * Fetch assessee reports with optional filters
 */
export const fetchAssesseeReports =
  ({
    organisation,
    location,
    division,
    level,
    assessor,
    assessee,
    startDate,
    endDate,
    page,
    limit,
    sort,
    order,
    search,
    filters,
  } = {}) =>
  async (dispatch, getState) => {
    try {
      const token = getState().token;

      const queryParams = new URLSearchParams({
        ...(organisation && { organisation }),
        ...(location && { location }),
        ...(division && { division }),
        ...(level && { level }),
        ...(assessor && { assessor }),
        ...(assessee && { assessee }),
        ...(startDate && { startDate }),
        ...(endDate && { endDate }),
        ...(page && { page }),
        ...(limit && { limit }),
        ...(sort && { sort }),
        ...(order && { order }),
        ...(search && { search }),
        ...(filters && { filters: JSON.stringify(filters) }),
      }).toString();

      const response = await fetch(
        `${BASE_URL}/assessments/reports/assessee-summary?${queryParams}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) throw new Error("Failed to fetch assessee reports");

      const reports = await response.json();
      dispatch(
        setAssesseeReports({
          data: reports.rows,
          total: reports.total,
        })
      );

      return reports;
    } catch (error) {
      console.error("Error while fetching assessee reports:", error);
      return { error: error.message };
    }
  };

/**
 * fetch all assessors for a given organisation/location
 */
export const fetchAssessors =
  ({ organisation, location }) =>
  async (dispatch, getState) => {
    try {
      const token = getState().token;

      const queryParams = new URLSearchParams({
        ...(organisation && { organisation }),
        ...(location && { location }),
      }).toString();

      const response = await fetch(
        `${BASE_URL}/assessments/assessors?${queryParams}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) throw new Error("Failed to fetch assessors");

      const assessors = await response.json();
      dispatch(setAssessors(assessors));

      return assessors;
    } catch (error) {
      console.error("Error while fetching assessors:", error);
      return { error: error.message };
    }
  };

/**
 * fetch all assessees for a given organisation/location
 */
export const fetchAssessees =
  ({ organisation, location }) =>
  async (dispatch, getState) => {
    try {
      const token = getState().token;

      const queryParams = new URLSearchParams({
        ...(organisation && { organisation }),
        ...(location && { location }),
      }).toString();

      const response = await fetch(
        `${BASE_URL}/assessments/assessees?${queryParams}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) throw new Error("Failed to fetch assessees");

      const assessees = await response.json();
      dispatch(setAssessees(assessees));

      return assessees;
    } catch (error) {
      console.error("Error while fetching assessees:", error);
      return { error: error.message };
    }
  };

/**
 * Fetch a single assessment by ID
 */
export const fetchAssessment = (id) => async (dispatch, getState) => {
  try {
    const token = getState().token;

    const response = await fetch(`${BASE_URL}/assessments/${id}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) throw new Error("Failed to fetch assessment");

    const assessment = await response.json();
    dispatch(setAssessment(assessment));
    return assessment;
  } catch (error) {
    console.error("Error while fetching assessment:", error);
    return { error: error.message };
  }
};

/**
 * Fetch all assessments with optional filters
 */

export const fetchAssessments =
  ({
    organisation,
    location,
    division,
    level,
    assessor,
    assessee,
    startDate,
    endDate,
    page,
    limit,
    sort,
    order,
    search,
    filters,
  } = {}) =>
  async (dispatch, getState) => {
    try {
      const token = getState().token;

      const queryParams = new URLSearchParams({
        ...(organisation && { organisation }),
        ...(location && { location }),
        ...(division && { division }),
        ...(level && { level }),
        ...(assessor && { assessor }),
        ...(assessee && { assessee }),
        ...(startDate && { startDate }),
        ...(endDate && { endDate }),
        ...(page && { page }),
        ...(limit && { limit }),
        ...(sort && { sort }),
        ...(order && { order }),
        ...(search && { search }),
        ...(filters && { filters: JSON.stringify(filters) }),
      }).toString();

      const response = await fetch(`${BASE_URL}/assessments?${queryParams}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) throw new Error("Failed to fetch assessments");

      const assessments = await response.json();
      dispatch(
        setAssessments({
          data: assessments.assessments,
          total: assessments.totalAssessments,
        })
      );

      return assessments;
    } catch (error) {
      console.error("Error while fetching assessments:", error);
      return { error: error.message };
    }
  };

/**
 * Create a new assessment
 */
export const createAssessment = (data) => async (dispatch, getState) => {
  try {
    const token = getState().token;

    const response = await fetch(`${BASE_URL}/assessments`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(data),
    });

    const payload = await response.json();

    if (!response.ok) {
      const errorMessage =
        payload?.error || payload?.message || "Failed to create assessment";
      return { error: errorMessage };
    }

    dispatch(setAssessment(payload));

    return payload;
  } catch (error) {
    console.error("Error while creating assessment:", error);
    return { error: error.message };
  }
};

/**
 * Update an assessment by ID
 */
export const updateAssessment = (id, data) => async (dispatch, getState) => {
  try {
    const token = getState().token;

    const response = await fetch(`${BASE_URL}/assessments/${id}`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(data),
    });

    const updatedAssessment = await response.json();

    if (!response.ok) {
      const errorMessage =
        updatedAssessment?.error ||
        updatedAssessment?.message ||
        "Failed to update assessment";
      return { error: errorMessage };
    }

    dispatch(setAssessment(updatedAssessment));
    dispatch(
      fetchAssessmentReports({
        organisation: data.organisation,
        location: data.location,
      })
    );

    return updatedAssessment;
  } catch (error) {
    console.error("Error while updating assessment:", error);
    return { error: error.message };
  }
};

/**
 * Delete an assessment by ID
 */
export const deleteAssessment = (id) => async (dispatch, getState) => {
  try {
    const token = getState().token;

    const response = await fetch(`${BASE_URL}/assessments/${id}`, {
      method: "DELETE",
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    const responseData = await response.json();

    if (!response.ok || !responseData) throw new Error(responseData.message);

    return { success: true, message: responseData.message };
  } catch (error) {
    console.error("Error while deleting assessment:", error);
    return {
      error: true,
      success: false,
      message: error.message,
    };
  }
};

/**
 * Link an assessee to assessments
 */

export const linkAssesseeToAssessments =
  (assesseeId, assessmentIds) => async (dispatch, getState) => {
    try {
      const token = getState().token;

      const response = await fetch(`${BASE_URL}/assessments/link-user`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          assessee: assesseeId,
          assessmentIds,
        }),
      });

      if (!response.ok)
        throw new Error("Failed to link assessee to assessments");

      const result = await response.json();

      return result;
    } catch (error) {
      console.error("Error while linking assessee to assessments:", error);
      return { error: error.message };
    }
  };
